import React, { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
moment.locale('es');

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 30000;

const Clock = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  return (
    <div className="text-white/70 text-2xl font-mono font-semibold" data-testid="tv-clock">
      {moment(time).format('HH:mm:ss')}
    </div>
  );
};

const LiveBadge = ({ lastUpdate }) => {
  const [ago, setAgo] = useState('0s');
  useEffect(() => {
    const tick = () => {
      if (!lastUpdate) return setAgo('0s');
      const diff = Math.max(0, Math.floor((Date.now() - lastUpdate) / 1000));
      setAgo(diff < 60 ? `${diff}s` : `${Math.floor(diff / 60)}m`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [lastUpdate]);
  return (
    <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-full" data-testid="tv-live-badge">
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 tv-live-dot" />
      <span className="text-emerald-300 text-sm font-semibold uppercase tracking-widest">Live</span>
      <span className="text-emerald-300/60 text-xs font-mono">· {ago}</span>
    </div>
  );
};

export function TvPabellon() {
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const params = new URLSearchParams(window.location.search);
  const pabKey = params.get('p') || 'estetica';

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/tv/pabellon/${pabKey}`);
      setData(res);
      setLastUpdate(Date.now());
    } catch (err) {
      console.error('TV fetch error:', err);
    }
  };

  const current = data?.current_surgery;
  // Nuevo: `surgeries` incluye TODAS con status. Fallback a upcoming legacy.
  const allSurgeries = data?.surgeries || data?.upcoming || [];
  const completed = data?.completed || 0;
  // Índice de la "siguiente" cirugía (primera upcoming) para resaltarla
  const nextIdx = allSurgeries.findIndex((s) => s.status === 'upcoming' || (!s.status && s !== current));

  return (
    <div className="min-h-screen bg-[#0C0C0E] text-white p-8 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white/90" data-testid="tv-pabellon-title">
            {data?.pavilion || 'Pabellón'}
          </h1>
          <p className="text-white/40 text-lg mt-1">
            {moment(data?.date).format('dddd D [de] MMMM, YYYY')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-4">
            <LiveBadge lastUpdate={lastUpdate} />
            <Clock />
          </div>
          <p className="text-white/40 text-sm">
            {data?.total || 0} cirugías hoy
            {completed > 0 && <span className="text-white/25"> · {completed} finalizadas</span>}
          </p>
        </div>
      </div>

      {/* Current Surgery - Big Display */}
      {current ? (
        <div className="bg-gradient-to-br from-[#1A2332] to-[#12171E] rounded-2xl p-8 mb-6 flex-shrink-0 tv-glow-current tv-fade-in" data-testid="tv-current-surgery">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-8 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-emerald-400 text-base font-semibold uppercase tracking-widest">En Cirugía Ahora</span>
          </div>
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2">
              <h2 className="text-6xl font-bold text-white tracking-tight leading-tight">
                {current.patient_name}
              </h2>
              <p className="text-3xl text-white/70 mt-3">{current.surgery_type}</p>
              {current.allergies && (
                <div className="flex items-center gap-3 mt-5 bg-red-950/50 border border-red-500/40 rounded-lg px-4 py-3" data-testid="tv-pabellon-current-allergy">
                  <span className="text-red-400 text-3xl font-bold">⚠</span>
                  <p className="text-red-200 text-xl font-semibold">Alergias: {current.allergies}</p>
                </div>
              )}
            </div>
            <div className="text-right space-y-4">
              <div>
                <p className="text-white/40 text-sm uppercase tracking-wider">Horario</p>
                <p className="text-5xl font-bold font-mono text-white">{current.time}</p>
                <p className="text-2xl text-white/40 font-mono">– {current.end_time}</p>
              </div>
              {current.doctor && (
                <div className="mt-4">
                  <p className="text-white/40 text-sm uppercase tracking-wider">Doctor</p>
                  <p className="text-2xl text-white/85">{current.doctor}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#1A2332]/50 rounded-2xl p-12 mb-6 border border-white/5 text-center flex-shrink-0">
          <p className="text-white/25 text-3xl">Sin cirugía en curso</p>
        </div>
      )}

      {/* All Surgeries List (agenda del día) */}
      <div className="flex-1">
        <h3 className="text-white/50 text-base uppercase tracking-widest mb-4 font-semibold">
          Agenda del Día ({allSurgeries.length})
        </h3>
        {allSurgeries.length > 0 ? (
          <div className="space-y-2">
            {/* Header row */}
            <div className="grid grid-cols-12 gap-4 px-6 py-3 text-white/30 text-lg uppercase tracking-wider font-semibold">
              <div className="col-span-1">Hora</div>
              <div className="col-span-2">Doctor</div>
              <div className="col-span-3">Paciente</div>
              <div className="col-span-3">Cirugía</div>
              <div className="col-span-3">Alergia</div>
            </div>
            {allSurgeries.map((s, idx) => {
              const status = s.status || 'upcoming';
              const isCompleted = status === 'completed';
              const isCurrent = status === 'current' && current && s.id === current.id;
              const isNextHighlight = idx === nextIdx && !current && status === 'upcoming';

              const rowClasses = isCompleted
                ? 'bg-[#0F1216] border border-white/5 opacity-45'
                : isCurrent
                ? 'bg-emerald-950/20 border border-emerald-500/30'
                : isNextHighlight
                ? 'tv-next-row border-2'
                : 'bg-[#14181E] border border-white/5';

              return (
                <div
                  key={s.id}
                  className={`grid grid-cols-12 gap-4 rounded-xl px-6 py-4 items-center tv-fade-in ${rowClasses}`}
                  data-testid={isNextHighlight ? 'tv-next-surgery' : isCompleted ? 'tv-completed-row' : undefined}
                >
                  <div className="col-span-1">
                    <p className={`text-3xl font-bold font-mono ${isCompleted ? 'text-white/40 line-through decoration-white/20' : 'text-white/85'}`}>{s.time}</p>
                    <p className="text-sm text-white/30 font-mono">{s.end_time}</p>
                    {isCompleted && (
                      <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-widest text-white/50 bg-white/10 px-2 py-0.5 rounded">Finalizada</span>
                    )}
                    {isNextHighlight && (
                      <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-widest text-blue-300 bg-blue-500/20 px-2 py-0.5 rounded">Siguiente</span>
                    )}
                    {isCurrent && (
                      <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-widest text-emerald-300 bg-emerald-500/20 px-2 py-0.5 rounded">En Curso</span>
                    )}
                  </div>
                  <div className="col-span-2">
                    <p className={`text-xl truncate ${isCompleted ? 'text-white/45' : 'text-white/75'}`}>{s.doctor || '—'}</p>
                  </div>
                  <div className="col-span-3">
                    <p className={`text-2xl font-semibold truncate ${isCompleted ? 'text-white/50 line-through decoration-white/25' : 'text-white/95'}`}>{s.patient_name}</p>
                  </div>
                  <div className="col-span-3">
                    <p className={`text-xl truncate ${isCompleted ? 'text-white/45' : 'text-white/75'}`}>{s.surgery_type}</p>
                  </div>
                  <div className="col-span-3" data-testid="tv-pabellon-allergy-cell">
                    {s.allergies ? (
                      <div className="flex items-center gap-2">
                        <span className={`text-xl font-bold flex-shrink-0 ${isCompleted ? 'text-red-400/40' : 'text-red-400'}`}>⚠</span>
                        <p className={`text-lg truncate font-semibold ${isCompleted ? 'text-red-300/40' : 'text-red-300/90'}`} title={s.allergies}>{s.allergies}</p>
                      </div>
                    ) : (
                      <p className="text-lg text-white/25">—</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-white/20 text-2xl">No hay cirugías programadas para hoy</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function TvRecuperacion() {
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const { data: res } = await axios.get(`${API_URL}/api/tv/recuperacion`);
      setData(res);
      setLastUpdate(Date.now());
    } catch (err) {
      console.error('TV fetch error:', err);
    }
  };

  const surgeries = data?.surgeries || [];

  return (
    <div className="min-h-screen bg-[#0C0C0E] text-white p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white/90" data-testid="tv-recup-title">
            Recuperación
          </h1>
          <p className="text-white/40 text-lg mt-1">
            {moment(data?.date).format('dddd D [de] MMMM, YYYY')}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-4">
            <LiveBadge lastUpdate={lastUpdate} />
            <Clock />
          </div>
          <p className="text-white/40 text-sm">{data?.total || 0} pacientes hoy</p>
        </div>
      </div>

      {/* Patients Table */}
      {surgeries.length > 0 ? (
        <div className="space-y-2">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 text-white/30 text-lg uppercase tracking-wider font-semibold">
            <div className="col-span-1">Hora</div>
            <div className="col-span-2">Doctor</div>
            <div className="col-span-3">Paciente</div>
            <div className="col-span-3">Cirugía</div>
            <div className="col-span-1">Alergia</div>
            <div className="col-span-2">Observaciones</div>
          </div>
          {surgeries.map((s) => (
            <div key={s.id} className="grid grid-cols-12 gap-4 bg-[#14181E] rounded-xl px-6 py-4 border border-white/5 items-center tv-fade-in">
              <div className="col-span-1">
                <p className="text-3xl font-bold font-mono text-white/85">{s.time}</p>
                <p className="text-sm text-white/30 font-mono">{s.end_time}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xl text-white/75 break-words leading-tight">{s.doctor || '—'}</p>
              </div>
              <div className="col-span-3">
                <p className="text-2xl font-semibold text-white/95 break-words leading-tight" data-testid="tv-recup-patient">{s.patient_name}</p>
                {s.atencion_id && (
                  <p className="text-sm text-emerald-400/70 font-mono mt-1" data-testid="tv-recup-atencion">
                    Atención #{s.atencion_id}
                  </p>
                )}
              </div>
              <div className="col-span-3">
                <p className="text-xl text-white/75 break-words leading-tight">{s.surgery_type}</p>
              </div>
              <div className="col-span-1" data-testid="tv-allergy-cell">
                {s.allergies ? (
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 text-xl font-bold flex-shrink-0">⚠</span>
                    <p className="text-base text-red-300/90 break-words font-semibold leading-tight" data-testid="tv-allergies-text" title={s.allergies}>{s.allergies}</p>
                  </div>
                ) : (
                  <p className="text-lg text-white/25">—</p>
                )}
              </div>
              <div className="col-span-2" data-testid="tv-observations-cell">
                {s.observations ? (
                  <p className="text-base text-white/80 break-words leading-tight" data-testid="tv-observations-text" title={s.observations}>{s.observations}</p>
                ) : (
                  <p className="text-base text-white/25">—</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-24">
          <p className="text-white/15 text-3xl">Sin pacientes programados hoy</p>
        </div>
      )}
    </div>
  );
}
